import React, {useState, useEffect} from 'react';
import {
  Grid,
  Button,
  CircularProgress
} from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import prism from 'prismjs';
import 'prismjs/plugins/line-numbers/prism-line-numbers';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';

import utils from '../../libs/utils';

const useStyles = makeStyles((theme) => ({
  viewer: {
    width: '100%',
    height: '100%',
    overflow: 'auto',
    margin: '0 !important'
  }
}));

interface CodeViewerProps {
  src: string
}

const CodeViewer = (props: CodeViewerProps) => {
  const { src } = props;
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [cancelToken, setCancelToken] = useState<CancelTokenSource>(axios.CancelToken.source());

  const classes = useStyles();
  const ext = src.split('.').reverse()[0].toLowerCase();
  const language = !!prism.languages[ext] ? ext : '';

  const loadSrc = () => {
    const _cancelToken = axios.CancelToken.source();
    cancelToken.cancel(); // cancel existing request
    setCancelToken(_cancelToken);

    setTimeout(async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        setContent('');

        if (src && src !== 'about:blank') {
          const { data } = await axios.get(src, {
            withCredentials: true,
            cancelToken: _cancelToken.token
          });
          setContent(data);
        }
      }
      catch (e) {
        if (!axios.isCancel(e)) {
          setErrorMessage(utils.getRequestErrorMessage(e));
        }
      }
      finally {
        setIsLoading(false);
      }
    });
  };

  const renderPrism = (el: HTMLElement | null) => {
    if (el) {
      if (!el.getAttribute('data-prism-initialized')) {
        el.setAttribute('data-prism-initialized', 'true');
        prism.highlightElement(el);
      }
    }
  };

  useEffect(() => {
    loadSrc();
  }, [src]);

  if (isLoading)
    return (
      <Grid container alignItems="center" justify="center" className="w-100 h-100">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );

  if (errorMessage)
    return (
      <Grid container alignItems="center" justify="center" className="w-100 h-100">
        <Grid item className="text-center">
          <h5>Something went wrong</h5>
          <p>{errorMessage}</p>
          <Button type="button" variant="contained" color="primary" onClick={loadSrc}>Try Again</Button>
        </Grid>
      </Grid>
    );

  return (
    <pre className={classes.viewer}><code ref={renderPrism} className={`line-numbers language-${language}`}>{content}</code></pre>
  );
};

export default CodeViewer;
